import { IInitialStateProps } from 'context/registerContext';
import { AssemblyModalities } from 'helpers/enums/Assembleia';
import { formataStringData } from 'helpers/utils/HTMLUtils';
import {
  allIsFalse,
  DEFAULT_VALID,
  DEFAULT_VALIDATION_MODEL,
  firstDateIsBefore,
  requiredIsValid,
  validaData,
  validaHora,
  ValidationModel,
} from 'helpers/utils/ValidationUtils';
import moment from 'moment';

export type DataValidatorType = {
  titleIsValid: () => ValidationModel;
  descriptionIsValid: () => ValidationModel;
  coopIsValid: () => ValidationModel;
  assemblyTypeIsValid: () => ValidationModel;
  assemblyModalityIsValid: () => ValidationModel;
  initialDateIsValid: () => ValidationModel;
  initialDateHibridIsValid: () => ValidationModel;
  endDateIsValid: () => ValidationModel;
  endDateHibridIsValid: () => ValidationModel;
  initialHourIsValid: () => ValidationModel;
  initialHourHibridIsValid: () => ValidationModel;
  endHourIsValid: () => ValidationModel;
  endHourHibridIsValid: () => ValidationModel;
  linkIsValid: () => ValidationModel;
  assemblyQuantityAssociateIsValid: () => ValidationModel;
  allIsValid: () => boolean;
};

export const DataValidation = (
  state: IInitialStateProps,
  reu?: boolean,
  day?: number
) => {
  const validator: Partial<DataValidatorType> = {};

  validator.titleIsValid = () => {
    const regex = /<[^>]*>/g;
    const value = String(state.data.campos.title).replace(regex, '');
    const required = requiredIsValid(value, 'Título é obrigatório');

    if (value.length > 100) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Tamanho do título superior a 100 caracteres'
      );
    }

    if (required.error) {
      return required;
    }
    return DEFAULT_VALID;
  };

  validator.descriptionIsValid = () => {
    const regex = /<[^>]*>/g;
    const value = String(state.data.campos.description).replace(regex, '');

    if (value.length > 300) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Tamanho da descrição superior a 300 caracteres'
      );
    }

    return DEFAULT_VALID;
  };

  validator.coopIsValid = () => {
    const value = state.data.campos.cooperative;
    const required = requiredIsValid(value, 'Cooperativa é obrigatória');

    if (required.error) {
      return required;
    }
    return DEFAULT_VALID;
  };

  validator.assemblyTypeIsValid = () => {
    const value = state.data.campos.type;
    const required = requiredIsValid(
      value,
      `Tipo de ${reu ? 'reunião' : 'assembleia'} é obrigatório`
    );

    if (required.error) {
      return required;
    }
    return DEFAULT_VALID;
  };

  validator.assemblyModalityIsValid = () => {
    const value = state.data.campos.modality;
    const required = requiredIsValid(
      value,
      `Modalidade da ${reu ? 'reunião' : 'assembleia'} é obrigatória`
    );

    if (required.error) {
      return required;
    }
    return DEFAULT_VALID;
  };

  validator.assemblyQuantityAssociateIsValid = () => {
    const value = state.data.campos.quantityAssociates;
    const required = requiredIsValid(
      value,
      'Quantidade de associados é obrigatória'
    );
    const modo = state.data.campos.modality;

    if (modo !== AssemblyModalities.OFFLINE) {
      if (required.error) {
        return required;
      }
      if (
        state.data.campos.quantityAssociates !== null &&
        Number(state.data.campos.quantityAssociates) === 0
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Quantidade de associados não pode ser 0'
        );
      }
    }

    return DEFAULT_VALID;
  };

  validator.linkIsValid = () => {
    const regex =
      /(https?:\/\/)(www\.)?(youtube\.com\/(watch\?v=|live\/)([a-zA-Z0-9_-]{11})|youtu\.be\/([a-zA-Z0-9_-]{11})|m\.youtube\.com\/(watch\?v=|live\/)([a-zA-Z0-9_-]{11}))|https?:\/\/(www\.)?vimeo\.com\/([0-9]{9})/g;
    const value = state.data.campos.link;
    const modo = state.data.campos.modality;

    if (modo !== AssemblyModalities.OFFLINE && value.length) {
      if (!regex.test(value)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'O link não é do YouTube ou Vimeo'
        );
      }
    }

    return DEFAULT_VALID;
  };

  // Date

  validator.initialDateIsValid = () => {
    const dataInicio = state.data.campos.startDate;
    const dataFim = state.data.campos.endDate;
    const isValid = validaData(dataInicio!);
    const required = requiredIsValid(
      dataInicio,
      'Data de início é obrigatória'
    );

    const hoje = new Date();
    const diaAtual = hoje.getDate();
    let dataMinimaPermitida: Date;

    if (diaAtual <= day!) {
      dataMinimaPermitida = new Date(
        hoje.getFullYear(),
        hoje.getMonth() - 1,
        1
      );
    } else {
      dataMinimaPermitida = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    }

    const dataInicioFormatada = new Date(
      new Date(moment(dataInicio, 'DD/MM/YYYY').toDate()).setUTCHours(
        23,
        59,
        59,
        999
      )
    );

    if (
      state.data.campos.modality! === AssemblyModalities.OFFLINE &&
      dataInicioFormatada < dataMinimaPermitida
    ) {
      return DEFAULT_VALIDATION_MODEL(true, `Data fora do intervalo permitido`);
    }

    if (
      ![AssemblyModalities.OFFLINE].includes(state.data.campos.modality!) &&
      dataInicioFormatada < new Date(new Date().setUTCHours(23, 59, 59, 999))
    ) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data início não pode ser inferior a data atual'
      );
    }

    if (required.error) {
      return required;
    }

    if (
      Date.parse(formataStringData(dataInicio).replace(/-/g, '/')) <
      Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
    ) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data início não pode ser inferior a 2018'
      );
    }

    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
    }

    if (firstDateIsBefore(dataFim!, dataInicio!)) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data de início deve ser anterior à data de término'
      );
    }

    return DEFAULT_VALID;
  };

  validator.initialDateHibridIsValid = () => {
    const dataInicio = state.data.campos.hybridStartDate;
    const dataFim = state.data.campos.hybridEndDate;
    const dataFimOnline = state.data.campos.endDate;
    const modo = state.data.campos.modality;
    const required = requiredIsValid(
      dataInicio,
      'Data de início é obrigatória'
    );

    if (
      [
        AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        AssemblyModalities.HIBRIDO_SIMULTANEO,
      ].includes(modo!)
    ) {
      if (
        new Date(
          new Date(moment(dataInicio, 'DD/MM/YYYY').toDate()).setUTCHours(
            23,
            59,
            59,
            999
          )
        ) < new Date(new Date().setUTCHours(23, 59, 59, 999))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data início não pode ser inferior a data atual'
        );
      }

      if (required.error) {
        return required;
      }

      if (
        Date.parse(formataStringData(dataInicio).replace(/-/g, '/')) <
        Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data início não pode ser inferior a 2018'
        );
      }

      const isValid = validaData(dataInicio!);
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }
      if (
        firstDateIsBefore(dataInicio!, dataFimOnline!) &&
        [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de início deve ser posterior à data de término presencial'
        );
      }
      if (firstDateIsBefore(dataFim!, dataInicio!)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de início deve ser anterior à data de término'
        );
      }
    }

    return DEFAULT_VALID;
  };

  validator.endDateIsValid = () => {
    const dataFim = state.data.campos.endDate;
    const isValid = validaData(dataFim!);
    const required = requiredIsValid(dataFim, 'Data de término é obrigatória');

    if (required.error) {
      return required;
    }

    if (
      Date.parse(formataStringData(dataFim).replace(/-/g, '/')) <
      Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
    ) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data de término não pode ser inferior a 2018'
      );
    }

    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
    }

    const dataInicio = state.data.campos.startDate;
    if (firstDateIsBefore(dataFim!, dataInicio!)) {
      return DEFAULT_VALIDATION_MODEL(
        true,
        'Data de término deve ser posterior à data de início'
      );
    }
    return DEFAULT_VALID;
  };

  validator.endDateHibridIsValid = () => {
    const dataFim = state.data.campos.hybridEndDate;
    const required = requiredIsValid(dataFim, 'Data de término é obrigatória');
    const isValid = validaData(dataFim!);
    const modo = state.data.campos.modality;

    if (
      [
        AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        AssemblyModalities.HIBRIDO_SIMULTANEO,
      ].includes(modo!)
    ) {
      if (required.error) {
        return required;
      }
      if (
        Date.parse(formataStringData(dataFim).replace(/-/g, '/')) <
        Date.parse(formataStringData('01/01/2018').replace(/-/g, '/'))
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de término não pode ser inferior a 2018'
        );
      }
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Data inválida');
      }
      const dataInicio = state.data.campos.hybridStartDate;

      if (firstDateIsBefore(dataFim!, dataInicio!)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Data de término deve ser posterior à data de início'
        );
      }
    }
    return DEFAULT_VALID;
  };

  // Hours

  validator.initialHourIsValid = () => {
    const initialDate = state.data.campos.startDate;
    const endDate = state.data.campos.endDate;
    const initialHour = state.data.campos.startHour;
    const endHour = state.data.campos.endHour;

    const required = requiredIsValid(
      initialHour,
      'Hora de início é obrigatória'
    );
    if (required.error) {
      return required;
    }
    const isValid = validaHora(initialHour);
    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
    }

    const inicio = moment(`${initialDate} ${initialHour}`, 'DD/MM/YYYY HH:mm');
    const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
    const fimCurrent = moment(moment(), 'DD/MM/YYYY HH:mm');

    if (![AssemblyModalities.OFFLINE].includes(state.data.campos.modality!)) {
      if (inicio.isSameOrBefore(fimCurrent)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Deve ser posterior ao horário atual'
        );
      }
    }

    if (
      initialDate &&
      validaData(initialDate) &&
      endHour &&
      validaHora(endHour) &&
      endDate &&
      validaData(endDate)
    ) {
      if (
        moment(initialDate, 'DD/MM/YYYY').isSame(moment(endDate, 'DD/MM/YYYY'))
      ) {
        if (inicio.isAfter(fim)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Deve ser anterior à hora de término'
          );
        }
      }
    }
    return DEFAULT_VALID;
  };

  validator.endHourIsValid = () => {
    const initialDate = state.data.campos.startDate;
    const endDate = state.data.campos.endDate;
    const initialHour = state.data.campos.startHour;
    const endHour = state.data.campos.endHour;

    const required = requiredIsValid(endHour, 'Hora de término é obrigatória');
    if (required.error) {
      return required;
    }
    const isValid = validaHora(endHour);
    if (!isValid) {
      return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
    }
    if (
      initialDate &&
      validaData(initialDate) &&
      initialHour &&
      validaHora(initialHour) &&
      endDate &&
      validaData(endDate)
    ) {
      if (
        moment(initialDate, 'DD/MM/YYYY').isSame(moment(endDate, 'DD/MM/YYYY'))
      ) {
        const inicio = moment(
          `${initialDate} ${initialHour}`,
          'DD/MM/YYYY HH:mm'
        );
        const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
        if (inicio.isAfter(fim)) {
          return DEFAULT_VALIDATION_MODEL(
            true,
            'Deve ser posterior à hora de início'
          );
        }
      }
    }
    return DEFAULT_VALID;
  };

  validator.initialHourHibridIsValid = () => {
    const initialDate = state.data.campos.hybridStartDate;
    const endDate = state.data.campos.hybridEndDate;
    const endDateOnline = state.data.campos.endDate;
    const initialHour = state.data.campos.hybridStartHour;
    const endHour = state.data.campos.hybridEndHour;
    const endHourOnline = state.data.campos.endHour;
    const modo = state.data.campos.modality;
    const required = requiredIsValid(
      initialHour,
      'Hora de início é obrigatória'
    );

    if (
      [
        AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        AssemblyModalities.HIBRIDO_SIMULTANEO,
      ].includes(modo!)
    ) {
      if (required.error) {
        return required;
      }
      const isValid = validaHora(initialHour);
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }

      const inicio = moment(
        `${initialDate} ${initialHour}`,
        'DD/MM/YYYY HH:mm'
      );

      const fimOnline = moment(
        `${endDateOnline} ${endHourOnline}`,
        'DD/MM/YYYY HH:mm'
      );

      const fimCurrent = moment(moment(), 'DD/MM/YYYY HH:mm');

      if (inicio.isSameOrBefore(fimCurrent)) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Deve ser posterior ao horário atual'
        );
      }

      if (
        inicio.isBefore(fimOnline) &&
        [AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL].includes(modo!)
      ) {
        return DEFAULT_VALIDATION_MODEL(
          true,
          'Deve ser posterior à hora de término presencial'
        );
      }

      if (
        initialDate &&
        validaData(initialDate) &&
        endHour &&
        validaHora(endHour) &&
        endDate &&
        validaData(endDate)
      ) {
        if (
          moment(initialDate, 'DD/MM/YYYY').isSame(
            moment(endDate, 'DD/MM/YYYY')
          )
        ) {
          const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');

          if (inicio.isAfter(fim)) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              'Deve ser anterior à hora de término'
            );
          }
        }
      }
    }
    return DEFAULT_VALID;
  };

  validator.endHourHibridIsValid = () => {
    const initialDate = state.data.campos.hybridStartDate;
    const endDate = state.data.campos.hybridEndDate;
    const initialHour = state.data.campos.hybridStartHour;
    const endHour = state.data.campos.hybridEndHour;
    const modo = state.data.campos.modality;
    const required = requiredIsValid(endHour, 'Hora de término é obrigatória');

    if (
      [
        AssemblyModalities.HIBRIDO_PRESENCIAL_DIGITAL,
        AssemblyModalities.HIBRIDO_DIGITAL_AGENCIA,
        AssemblyModalities.HIBRIDO_SIMULTANEO,
      ].includes(modo!)
    ) {
      if (required.error) {
        return required;
      }
      const isValid = validaHora(endHour);
      if (!isValid) {
        return DEFAULT_VALIDATION_MODEL(true, 'Hora inválida');
      }
      if (
        initialDate &&
        validaData(initialDate) &&
        initialHour &&
        validaHora(initialHour) &&
        endDate &&
        validaData(endDate)
      ) {
        if (
          moment(initialDate, 'DD/MM/YYYY').isSame(
            moment(endDate, 'DD/MM/YYYY')
          )
        ) {
          const inicio = moment(
            `${initialDate} ${initialHour}`,
            'DD/MM/YYYY HH:mm'
          );
          const fim = moment(`${endDate} ${endHour}`, 'DD/MM/YYYY HH:mm');
          if (inicio.isAfter(fim)) {
            return DEFAULT_VALIDATION_MODEL(
              true,
              'Deve ser posterior à hora de início'
            );
          }
        }
      }
    }

    return DEFAULT_VALID;
  };

  validator.allIsValid = () => {
    return allIsFalse([
      validator?.titleIsValid!().error,
      validator?.descriptionIsValid!().error,
      validator?.coopIsValid!().error,
      validator?.assemblyTypeIsValid!().error,
      validator?.assemblyModalityIsValid!().error,
      validator?.linkIsValid!().error,
      validator?.assemblyQuantityAssociateIsValid!().error,
      validator?.initialDateIsValid!().error,
      validator?.endDateIsValid!().error,
      validator?.initialHourIsValid!().error,
      validator?.endHourIsValid!().error,
      validator?.initialDateHibridIsValid!().error,
      validator?.endDateHibridIsValid!().error,
      validator?.initialHourHibridIsValid!().error,
      validator?.endHourHibridIsValid!().error,
    ]);
  };

  return validator as DataValidatorType;
};
